import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  MenubarContext,
  ParentMenuContext
} from '../../../../components/Menubar/contexts';
import { getCurrentClassroomId } from '../../selectors/users';
import { getClassroom } from '../../../../firebase/classrooms';
import { listProjects } from '../../../../firebase/projects';
import NavFlyout from './NavFlyout';

/** One student's flyout: lazy-loads their sketches (incl. Private, per the staff read rule). */
const StudentSketches = ({
  student,
  classroomId,
  onNavigate,
  triggerProps
}) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState(null); // null = loading

  const load = () => {
    // Scoped to the classroom the instructor is currently in: a student's sketches from another
    // classroom (or from home) are not this classroom's business.
    listProjects(student.username, true, { classroomId })
      .then((res) => setProjects(res.projects))
      .catch(() => setProjects([]));
  };

  return (
    <NavFlyout
      label={student.username}
      loading={projects === null}
      onOpen={load}
      triggerProps={triggerProps}
    >
      {projects && projects.length === 0 && (
        <li className="nav__dropdown-item nav__flyout-status">
          {t('ClassroomMenu.NoSketches', {
            defaultValue: 'No sketches yet'
          })}
        </li>
      )}
      {(projects || []).map((p) => (
        <li className="nav__dropdown-item" key={p.id}>
          {/* A real <Link> navigates reliably and keeps the href for open-in-new-tab.
              onMouseDown preventDefault (plain left-click only) keeps focus on the flyout trigger:
              without it, pressing the link moves focus off the trigger, whose blur fires the
              Menubar's hide-timeout (~10ms) and removes this link BEFORE the click lands — so the
              click was being swallowed. state.confirmed:true bypasses IDEView's unsaved-changes
              <Prompt> (same escape hatch newProject() uses). */}
          <Link
            to={{
              pathname: `/${student.username}/sketches/${p.id}`,
              state: { confirmed: true }
            }}
            role="menuitem"
            tabIndex={-1}
            onMouseDown={(e) => {
              if (
                e.button === 0 &&
                !e.metaKey &&
                !e.ctrlKey &&
                !e.shiftKey &&
                !e.altKey
              ) {
                e.preventDefault();
              }
            }}
            onClick={onNavigate}
          >
            {p.name}
            {p.visibility === 'Private'
              ? ` (${t('ViewClassroomModal.Private', {
                  defaultValue: 'Private'
                })})`
              : ''}
          </Link>
        </li>
      ))}
    </NavFlyout>
  );
};

StudentSketches.propTypes = {
  student: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired
  }).isRequired,
  classroomId: PropTypes.string.isRequired,
  onNavigate: PropTypes.func.isRequired,
  triggerProps: PropTypes.shape({
    onFocus: PropTypes.func,
    onBlur: PropTypes.func
  })
};

StudentSketches.defaultProps = {
  triggerProps: {}
};

/**
 * "View classroom" as a cascading flyout (desktop): current classroom → students → their sketches.
 * Staff-only (rendered inside the isStaff-gated slot in Nav). Each level lazy-loads with a spinner.
 * Clicking a sketch opens it in the editor and closes the menu.
 */
const ClassroomStudentsMenu = () => {
  const { t } = useTranslation();
  const currentClassroomId = useSelector(getCurrentClassroomId);
  const { setMenuOpen, createMenuItemHandlers } = useContext(MenubarContext);
  const parentMenu = useContext(ParentMenuContext);
  const [classroom, setClassroom] = useState(null); // null = loading, false = none/unavailable

  // Drop the cached students when the user switches classroom ("My classroom"); the keyed NavFlyout
  // below then lazy-loads the new one on its next open.
  useEffect(() => setClassroom(null), [currentClassroomId]);

  // Reuse the Menubar's own item focus/blur handlers on our flyout triggers (but NOT its
  // menu-closing onMouseUp) so focusing a trigger cancels the Menubar's pending hide-timeout the
  // same way a real MenubarItem would — otherwise the menu closes ~10ms after the flyout opens.
  const itemHandlers = createMenuItemHandlers
    ? createMenuItemHandlers(parentMenu)
    : {};
  const triggerProps = {
    onFocus: itemHandlers.onFocus,
    onBlur: itemHandlers.onBlur
  };

  const noClassroom = !currentClassroomId;

  const loadStudents = () => {
    if (!currentClassroomId) {
      setClassroom(false);
      return;
    }
    getClassroom(currentClassroomId)
      .then((room) => setClassroom(room || false))
      .catch(() => setClassroom(false));
  };

  const closeMenu = () => setMenuOpen('none');

  const students = classroom && classroom.students ? classroom.students : [];

  return (
    <NavFlyout
      // Remount when "My classroom" switches rooms: NavFlyout only calls onOpen on its FIRST open,
      // so a fresh key is what makes the students reload for the new classroom.
      key={currentClassroomId}
      label={t('Nav.Classroom.View', { defaultValue: 'View classroom' })}
      loading={!noClassroom && classroom === null}
      disabled={noClassroom}
      onOpen={loadStudents}
      triggerProps={triggerProps}
    >
      {classroom !== null && students.length === 0 && (
        <li className="nav__dropdown-item nav__flyout-status">
          {t('ClassroomMenu.NoStudents', {
            defaultValue: 'No students yet'
          })}
        </li>
      )}
      {students.map((s) => (
        <StudentSketches
          key={s.uid}
          student={s}
          classroomId={currentClassroomId}
          onNavigate={closeMenu}
          triggerProps={triggerProps}
        />
      ))}
    </NavFlyout>
  );
};

export default ClassroomStudentsMenu;
